import { ChartDataSet } from '~/utils/charts/unovis'
import { formatDate, parseISODate } from '~/utils/date'
import { shortenLargeNumber } from '~/utils/string'
import { labelDateFormat } from '../reports'

export type TooltipFormatter = (d: string | number, ...args: unknown[]) => string | number

export type TooltipOptions = {
  formatTooltipX?: TooltipFormatter
  formatTooltipY?: TooltipFormatter
  [key: string]: unknown
}

const ISO_DATE_LABEL_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[Tt ][\d:.+-Zz]+)?$/

export const getThemeColors = (
  config: Record<string, string | Record<string, string>>,
  prefix = ''
): Record<string, string> => {
  const colors: Record<string, string> = {}

  Object.entries(config).forEach(([token, value]) => {
    if (typeof value === 'string') {
      colors[prefix + token] = value
    } else {
      Object.assign(colors, getThemeColors(value, `${prefix}${token}-`))
    }
  })

  return colors
}

export const seriesByType = (
  chartType: string,
  dataSets: Array<ChartDataSet>,
  type: 'bar' | 'line'
): Array<number> => {
  if (chartType === type) {
    return dataSets.map((_, index) => index)
  }

  if (chartType !== 'axis-mixed') {
    return []
  }

  return dataSets
    .map((dataset, index) => ({
      index,
      chartType: dataset.chartType ?? 'bar'
    }))
    .filter((dataset) => dataset.chartType === type)
    .map((dataset) => dataset.index)
}

export const selectCrosshairIndices = (options: {
  chartType: string
  dataSets: Array<ChartDataSet>
  stacked: boolean
}): { dataIndices: Array<number>; useStacked: boolean } => {
  const { chartType, dataSets, stacked } = options
  const barIndices = seriesByType(chartType, dataSets, 'bar')
  const lineIndices = seriesByType(chartType, dataSets, 'line')
  const useStacked = chartType !== 'axis-mixed' && stacked && barIndices.length > 0
  const dataIndices = barIndices.length
    ? barIndices
    : lineIndices.length
      ? lineIndices
      : dataSets.map((_, index) => index)
  return { dataIndices, useStacked }
}

export const lineGradientId = (seriesIndex: number): string => {
  return `z-chart-line-gradient-${seriesIndex}`
}

export const formatTooltipTitle = (label: string, xTickFormat: string): string => {
  if (!label) return ''
  if (!ISO_DATE_LABEL_PATTERN.test(label)) {
    return label
  }
  const parsed = parseISODate(label)
  if (Number.isNaN(parsed.getTime())) {
    return label
  }
  return formatDate(parsed, xTickFormat)
}

export const computeXTickFormat = (labels: Array<string>): string => {
  if (!labels.length) return 'll'
  const firstLabel = labels[0]
  const lastLabel = labels[labels.length - 1]
  if (!firstLabel || !lastLabel) return 'll'

  return labelDateFormat(lastLabel, firstLabel)
}

export const buildHumanizedTooltips = (options: {
  humanizeTooltips: boolean
  tooltipOptions?: TooltipOptions | null
}): Record<string, TooltipFormatter | unknown> => {
  const { humanizeTooltips, tooltipOptions } = options

  if (!humanizeTooltips) {
    return tooltipOptions ?? {}
  }

  const tooltipFunctions = ['formatTooltipX', 'formatTooltipY']
  const shortenIfNumber = (value: string | number) =>
    Number.isNaN(Number(value)) ? value : shortenLargeNumber(value)
  const humanizedTooltipOptions: Record<string, TooltipFormatter> = {}

  tooltipFunctions.forEach((key) => {
    tooltipOptions && typeof tooltipOptions[key] === 'function'
      ? (humanizedTooltipOptions[key] = (d: string | number, ...args: unknown[]) => {
          const value = tooltipOptions[key] as TooltipFormatter
          return shortenIfNumber(value(d, ...args))
        })
      : (humanizedTooltipOptions[key] = (d: string | number) => shortenIfNumber(d))
  })

  return tooltipOptions
    ? { ...tooltipOptions, ...humanizedTooltipOptions }
    : humanizedTooltipOptions
}

export const buildYDomain = (
  yAxisMin: number | null,
  yAxisMax: number | null
): [number | undefined, number | undefined] | null => {
  if (yAxisMin === null && yAxisMax === null) {
    return null
  }
  return [yAxisMin ?? undefined, yAxisMax ?? undefined]
}

export const renderTooltipHtml = (options: {
  datum: { label: string; x: number }
  dataSets: Array<ChartDataSet>
  palette: Array<string>
  xTickFormat: string
  tooltipOptions?: TooltipOptions | null
  humanizedTooltips: Record<string, TooltipFormatter | unknown>
}): string => {
  const { datum, dataSets, palette, xTickFormat, tooltipOptions, humanizedTooltips } = options

  if (!datum) return ''
  const rawTitle = datum.label
  const formatterX = tooltipOptions?.formatTooltipX
  const formatterY = humanizedTooltips.formatTooltipY as TooltipFormatter | undefined
  const title = formatterX ? formatterX(rawTitle) : formatTooltipTitle(rawTitle, xTickFormat)

  const rows = dataSets.map((dataset, index) => {
    const value = dataset.values[datum.x]
    const formattedValue = formatterY ? formatterY(value ?? 0, dataset, index) : value
    const displayValue = formattedValue ?? value ?? 0
    const swatchColor = palette[index] || palette[0]
    const formattedLabel = dataset.name ? dataset.name.toUpperCase() : ''
    return `
      <div class="z-chart-tooltip__series" style="--series-color: ${swatchColor}">
        <span class="z-chart-tooltip__swatch"></span>
        <div class="z-chart-tooltip__series-body">
          <span class="z-chart-tooltip__value">${displayValue}</span>
          <span class="z-chart-tooltip__label">${formattedLabel}</span>
        </div>
      </div>
    `
  })

  return `
    <div class="z-chart-tooltip__content">
      <div class="z-chart-tooltip__title">${title}</div>
      ${rows.join('')}
    </div>
  `
}
